import React, { useState, useEffect, useRef } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Icon } from '@mui/material';
import { get, last, size } from 'global/lib/lodash';
import useStyles from './styles';
import { GridContainer, CenterContent } from 'global/components/perform-grid';
import useRetrieveData from 'global/hooks/useRetrieveData';
import Form from 'global/components/form';
import Alert from '@mui/material/Alert';
import { flattenAllRealmsWithRestricted, validateEmail } from 'global/utils';
import { SETTINGS } from '../../admin/account-settings';
import { sortRealmsAlphabetically } from 'global/components/droprealms';
import sendData from 'redux/actions/sendData';
import { getViewStyle } from 'redux/actions/view';
import { useDispatch } from 'react-redux';
import Image from 'global/components/image';
import PerformLogo from 'assets/logo.svg';

export default (props) => {
  const [alerts, setAlerts] = useState();
  let [formState, setFormState] = useState({});
  const [availRealms, setAvailRealms] = useState([]);
  const [errors, setErrors] = useState({ email: false });
  const [loading, setLoading] = useState();
  const [logo, setLogo] = useState('');
  const formRef = useRef();

  const dispatch = useDispatch();
  const classes = useStyles();

  const { id, tier } = get(props, 'match.params', {});

  const isBuilderTier = (tier || '').toLowerCase() === 'builder';
  const isCommunityTier = (tier || '').toLowerCase() === 'community';
  const isDivisionTier = (tier || '').toLowerCase() === 'division';

  const accountSettings = useRetrieveData({ url: `/account-settings?realm_id=${id}` }, { build: ['accountSettings'] });
  const realms = useRetrieveData({ url: `/realms/${id}` }, { build: ['realms'], params: { selfRegister: 1, include: 'realms,realms.realms' } });
  const usergroups = useRetrieveData({ url: `/user-groups` }, { build: ['userGroups'], params: { filter: { realm_id: id } } });

  const setAlert = (message, severity = 'error') => {
    if (!message) {
      return setAlerts();
    }
    setAlerts({
      message,
      severity,
    });
  };

  const onFormState = (fs) => {
    setAlert();
    if (isCommunityTier) {
      if (get(fs, 'div_id') !== get(formState, 'div_id')) {
        //need to do this so that the realm_ids don't get out of sync, if they choose a division, and then a community, and then change a division again without changing a comminuty
        fs.realm_id = null;
        formState.div_id = fs.div_id;
        formRef.current.updateState('realm_id', fs.realm_id, false, false);
      }
    }
    setFormState(fs);
  };

  const emailPassesAllowedDomains = (email = '') => {
    const allowedDomainsJSON = accountSettings.data.find((d) => d.name === SETTINGS.allowedDomains).value;
    const allowedDomains = JSON.parse(allowedDomainsJSON);
    const allowedStrings = allowedDomains.map((ad) => last((ad || '').toString().toLowerCase().split('@')));
    return allowedStrings.indexOf(last(email.toLowerCase().split('@'))) > -1;
  };

  const onSubmit = async () => {
    setAlert();
    setLoading(true);

    try {
      const email = `${get(formState, 'email', '')}`.replace(/\s/g, '');
      const name = get(formState, 'name');
      const realm_id = get(formState, 'realm_id');

      if (!email || !name || (!realm_id && !isBuilderTier)) {
        throw new Error('Please fill out all fields');
      }

      if (!validateEmail(email)) {
        throw new Error('Invalid email, please try again.');
      }

      if (!emailPassesAllowedDomains(email)) {
        throw new Error(`This email address does not meet the acceptance criteria. No new account was created.`);
      }

      const userGroups = formState.userGroups ? { data: [{ type: 'user-groups', id: `${formState.userGroups}` }] } : undefined;

      await sendData({
        url: `/users`,
        method: 'post',
        data: {
          data: {
            type: 'users',
            attributes: {
              name,
              email,
              realm_id: id,
              self_register_email: true,
            },
            relationships: {
              realms: {
                data: [{ type: 'realms', id: isBuilderTier ? id : realm_id }],
              },
              userGroups,
            },
          },
        },
      });

      get(formRef, 'current.clearState')();
      setLoading(false);
      setAlert('Your account has been created. Please check your email for further instructions.', 'success');
    } catch (err) {
      let message = get(err, 'response.data.message');
      if (message) {
        if (message.indexOf('Duplicate entry') > -1) {
          message = `Error: User already exists.`;
        }
      } else {
        message = err.message;
      }
      setAlert(message);
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(getViewStyle(id));
  }, []);

  useEffect(() => {
    if (!realms.loading && !accountSettings.loading) {
      const allowedToRegister = accountSettings.data.find((d) => d.name === SETTINGS.selfRegistration);
      if (get(allowedToRegister, 'value') !== '1') {
        return notSupposedToBeHere();
      }

      const _logo = accountSettings.data.find((d) => d.name === 'Logo');
      setLogo(get(_logo, 'value', ''));

      const restrictedJSON = accountSettings.data.find((d) => d.name === SETTINGS.accessRestrictions).value;
      const resAccess = JSON.parse(restrictedJSON);
      const restricted = flattenAllRealmsWithRestricted(realms.data, resAccess, { tier: isCommunityTier ? undefined : (tier || '').toLowerCase() });
      setAvailRealms(sortRealmsAlphabetically(restricted));
    }
  }, [realms.data, realms.loading, accountSettings.loading]);

  useEffect(() => {
    setAlert(get(accountSettings, 'error.message'));
  }, [accountSettings.error]);

  const notSupposedToBeHere = () => {
    alert('Sorry you are not allowed to register. Returning Home.');
    window.location.href = '/home';
  };

  const getTitle = () => {
    if (isBuilderTier) {
      return 'Corporate Account Registration';
    } else if (isDivisionTier) {
      return 'Divisional Account Registration';
    } else {
      return 'Account Registration';
    }
  };

  if (!id) {
    notSupposedToBeHere();
  }

  return accountSettings.loading ? null : (
    <div className={classes.root}>
      <AppBar position="static" className={classes.contentRoot}>
        <Toolbar>
          <Typography variant="h6" color="inherit">
            {props.pageTitle}
          </Typography>
          <div className={classes.grow} />
        </Toolbar>
      </AppBar>

      <GridContainer>
        <CenterContent fullWidth>
          <div className={classes.content}>
            {logo !== null && (
              <div className={classes.logoHeader}>
                <Image onError={() => setLogo(null)} autoReload={false} src={logo} useNaturalDims={false} unsetHeight unsetWidth />
              </div>
            )}
            {alerts && (
              <Alert
                severity={get(alerts, 'severity', 'error')}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setAlerts();
                    }}
                  >
                    <Icon>close</Icon>
                  </IconButton>
                }
              >
                {get(alerts, 'message', '')}
              </Alert>
            )}

            <Form
              ref={formRef}
              onFormState={onFormState}
              onSubmit={onSubmit}
              loading={accountSettings.loading || loading}
              showAllScreens={true}
              canSubmit={true}
              screens={[
                {
                  title: getTitle(),
                  description:
                    'Configure your account on PERFORM by entering the following information, including a valid business email address. After submitting, an email will be sent to you for further instructions.',
                  inputs: [
                    {
                      id: 'name',
                      type: 'TextField',
                      defaultValue: () => get(formState, 'name'),
                      props: {
                        label: 'First and Last Name',
                      },
                    },
                    {
                      id: 'email',
                      type: 'TextField',
                      defaultValue: () => get(formState, 'email'),
                      props: {
                        label: 'Email',
                        error: get(errors, 'email'),
                        helperText: get(errors, 'email') && 'Invalid email',
                      },
                    },
                    !size(usergroups.data)
                      ? null
                      : {
                          id: 'userGroups',
                          type: 'Select',
                          defaultValue: () => get(formState, 'userGroups'),
                          data: () => usergroups.data,
                          item: (d) => ({ value: d.id, title: d.name }),
                          props: {
                            label: 'User Group'
                          },
                        },
                    isBuilderTier
                      ? null
                      : {
                          id: isCommunityTier ? 'div_id' : 'realm_id',
                          type: 'Select',
                          defaultValue: () => get(formState, 'realm_id'),
                          data: () => (isCommunityTier ? availRealms.filter((r) => r.tier === 'division') : availRealms),
                          item: (d) => ({ value: d.id, title: d.name }),
                          props: {
                            label: isCommunityTier ? 'Divisions' : 'Locations',
                          },
                        },
                    isCommunityTier &&
                      get(formState, 'div_id') && {
                        id: 'realm_id',
                        type: 'Select',
                        defaultValue: () => get(formState, 'realm_id'),
                        data: () => availRealms.filter((r) => r.parentId == get(formState, 'div_id')),
                        item: (d) => ({ value: d.id, title: d.name }),
                        props: {
                          label: 'Communities',
                        },
                      },
                  ].filter((f) => f),
                },
              ]}
            />

            <div className={classes.performLogo}>
              <Image autoReload={false} src={PerformLogo} autosize useNaturalDims={false} width={150} />
            </div>
          </div>
        </CenterContent>
      </GridContainer>
    </div>
  );
};
